import { Check, Copy, Eye, EyeOff } from 'lucide-react'
import { type KeyboardEvent, type MouseEvent, useState } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { cn } from '@/lib/utils'

type CopyFieldProps = {
  value: string | null | undefined
  className?: string
  isSensitive?: boolean
  placeholder?: string
  size?: 'default' | 'sm'
}

const sizeStyles = {
  default: {
    container: 'h-9 px-3 text-sm gap-2',
    icon: 'size-4',
    iconWrapper: 'size-4',
    eyeButton: 'p-1',
    iconsGap: 'gap-2',
  },
  sm: {
    container: 'h-6 px-2 text-xs gap-1.5',
    icon: 'size-3.5',
    iconWrapper: 'size-3.5',
    eyeButton: 'p-0.5',
    iconsGap: 'gap-1.5',
  },
}

export const CopyField = ({
  value,
  className,
  isSensitive = false,
  placeholder = 'N/A',
  size = 'default',
}: CopyFieldProps) => {
  const [isCopied, setIsCopied] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const isEmpty = !value
  const s = sizeStyles[size]

  const handleCopy = () => {
    if (isEmpty) {
      return
    }

    navigator.clipboard.writeText(value).then(
      () => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
        toast(
          <ToasterContent variant="success">Copied to clipboard</ToasterContent>
        )
      },
      () => setIsCopied(false)
    )
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleCopy()
    }
  }

  const handleToggleVisibility = (e: MouseEvent) => {
    e.stopPropagation()
    setIsVisible((prev) => !prev)
  }

  const maskedValue = '•'.repeat(Math.min(value?.length ?? 0, 32))

  if (isEmpty) {
    return (
      <div
        aria-disabled="true"
        className={cn(
          'flex w-fit max-w-full min-w-0 items-center overflow-hidden rounded-md border border-input bg-transparent font-mono text-muted-foreground italic',
          s.container,
          className
        )}
      >
        {placeholder}
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'flex w-fit max-w-full min-w-0 cursor-pointer items-center overflow-hidden rounded-md border border-input bg-transparent transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        s.container,
        className
      )}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
    >
      <span
        className="min-w-0 flex-1 truncate font-mono"
        title={isSensitive && !isVisible ? undefined : value}
      >
        {isSensitive && !isVisible ? maskedValue : value}
      </span>
      <div
        className={cn('flex shrink-0 self-stretch items-center', s.iconsGap)}
      >
        {isSensitive ? (
          <>
            <button
              type="button"
              aria-label={isVisible ? 'Hide value' : 'Show value'}
              aria-pressed={isVisible}
              className={cn(
                'rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                s.eyeButton
              )}
              onClick={handleToggleVisibility}
            >
              {isVisible ? (
                <EyeOff aria-hidden="true" className={s.icon} />
              ) : (
                <Eye aria-hidden="true" className={s.icon} />
              )}
            </button>
            <div className="w-px self-stretch bg-border" />
          </>
        ) : null}
        <div className={cn('relative text-muted-foreground', s.iconWrapper)}>
          <Check
            aria-hidden="true"
            className={cn(
              'absolute inset-0 text-green-500 transition-opacity',
              s.icon,
              isCopied ? 'opacity-100' : 'opacity-0'
            )}
          />
          <Copy
            aria-hidden="true"
            className={cn(
              'absolute inset-0 transition-opacity',
              s.icon,
              isCopied ? 'opacity-0' : 'opacity-100'
            )}
          />
        </div>
      </div>
    </div>
  )
}
